// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { createSnackbar } from '@automator/shared/utils/create-snackbar';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-field-click-to-copy' )
export class AutomatorBuilderFieldClickToCopy extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The duration of the snackbar in milliseconds.
	 *
	 * @type {Number}
	 */
	snackbarDuration = 3000;

	/**
	 * The ID of the target field whose value will be copied.
	 *
	 * @type {String}
	 */
	@property( { type: String, attribute: 'field-id' } ) 
	accessor fieldID;

	/**
	 * Tooltip text shown when hovering over the button.
	 *
	 * @type {String}
	 */
	@property( { type: String } ) 
	accessor tooltip = escapeHTML( __( 'Click to copy', 'uncanny-automator' ) );

	/**
	 * Current state of the button. Valid values are:
	 * - 'default': Normal state.
	 * - 'copied': Indicates that the text was copied.
	 *
	 * @type {String}
	 */
	@state( { type: String } ) 
	accessor buttonState = 'default';

	/**
	 * Render click to copy button.
	 *
	 * @return {TemplateResult} The template result
	 */
	render() {
		const buttonStyle = {
			color: this.buttonState === 'copied' ? 'success' : '',
			icon: this.buttonState === 'copied' ? 'check' : 'copy'
		}

		return html`
			<uo-tooltip>
				<span class="nowrap">${ this.tooltip }</span>

				<span slot="target">
					<uo-button
						@click="${ this.handleClick }"
						color=${ buttonStyle.color }
						size="small"
					>
						<uo-icon id="${ buttonStyle.icon }"></uo-icon>
					</uo-button>
				</span>
			</uo-tooltip>
		`;
	}

	/**
	 * Handles the click event on the button.
	 * Attempts to copy the value of the target field to the clipboard.
	 * Uses the secure Clipboard API.
	 *
	 * @async
	 * @returns {Promise<void>}
	 */
	async handleClick() {
		// Get the target field
		const targetField = document.getElementById( this.fieldID );
	  
		// Abort if the target field is not found.
		if ( ! isDefined( targetField ) ) {
			// Abort if the target field is not found.
			return;
		}
	  
		try {
			// Write the target field's value to the clipboard.
			await navigator.clipboard.writeText( targetField.value );

			// Toggle the button state to "copied".
			this.toggleState();
		} catch ( err ) {
			console.error( 'Clipboard copy failed:', err );
		}
	}

	/**
	 * Toggles the button state to 'copied', displays a snackbar with a success message, and resets the state back to 'default' after 3 seconds.
	 */
	toggleState() {
		// Toggle the button state to "copied".
		this.buttonState = 'copied';
	
		// Get the label element for the target field.
		const labelElement = document.querySelector( `[for="${ this.fieldID }"]` );

		// Define the success message. Include the label text in the message if it exists.
		const message =
			isDefined( labelElement ) && labelElement.textContent
				? sprintf(
					escapeHTML( __( '"%s" copied to clipboard', 'uncanny-automator' ) ),
					labelElement.textContent
				)
				: escapeHTML( __( 'Copied to clipboard', 'uncanny-automator' ) );
	
		// Show a snackbar with the success message.
		createSnackbar( {
			iconId: 'check',
			message: message,
			duration: this.snackbarDuration,
		} );
	
		// Reset the button state to "default" after a few seconds.
		setTimeout( () => {
			this.buttonState = 'default';
		}, this.snackbarDuration );
	}
}